import React, { useState } from 'react';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MenuIcon from '@mui/icons-material/Menu';
import LanguageIcon from '@mui/icons-material/Language';
import Box from '@mui/material/Box';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    LOCALE_STORAGE_KEY,
    SUPPORTED_LOCALES,
    localizePath,
    type Locale,
} from '../i18n/config';
import { useI18n } from '../i18n/I18nProvider';
import { loadLocaleBundle } from '../i18n/localeLoader';
import firebaseService from '../Services/firebaseService';
import { SUPPORT_MAILTO } from '../Config/support';

interface HeaderProps {
    onPrivacyClick: () => void;
    onTermsClick: () => void;
    onFaqClick?: () => void;
}

const LOCALE_BADGE_LABEL: Record<Locale, string> = {
    en: 'EN',
    es: 'ES',
    id: 'ID',
    ja: '日本',
    ko: 'KO',
    fil: 'FIL',
    pt: 'PT',
    ru: 'РУ',
    th: 'ไทย',
    tr: 'TR',
    ar: 'عر',
    fr: 'FR',
    de: 'DE',
    it: 'IT',
    pl: 'PL',
    vi: 'VI',
};

const EN_LANGUAGE_SORT_NAME: Record<Locale, string> = {
    ar: 'Arabic',
    en: 'English',
    es: 'Spanish',
    fil: 'Filipino',
    id: 'Indonesian',
    ja: 'Japanese',
    ko: 'Korean',
    pt: 'Portuguese',
    ru: 'Russian',
    th: 'Thai',
    tr: 'Turkish',
    fr: 'French',
    de: 'German',
    it: 'Italian',
    pl: 'Polish',
    vi: 'Vietnamese',
};

const LANGUAGE_MENU_PRIORITY: Partial<Record<Locale, number>> = {
    en: 0,
    es: 1,
};

const Header: React.FC<HeaderProps> = ({
    onPrivacyClick,
    onTermsClick,
    onFaqClick,
}) => {
    const [mainMenuAnchorEl, setMainMenuAnchorEl] =
        useState<null | HTMLElement>(null);
    const [languageMenuAnchorEl, setLanguageMenuAnchorEl] =
        useState<null | HTMLElement>(null);
    const mainMenuOpen = Boolean(mainMenuAnchorEl);
    const languageMenuOpen = Boolean(languageMenuAnchorEl);
    const navigate = useNavigate();
    const location = useLocation();
    const { locale, t, languageSwitcherOptions } = useI18n();
    const sortedLanguageLocales = React.useMemo(
        () =>
            [...SUPPORTED_LOCALES].sort((a, b) => {
                const priorityA =
                    LANGUAGE_MENU_PRIORITY[a] ?? Number.POSITIVE_INFINITY;
                const priorityB =
                    LANGUAGE_MENU_PRIORITY[b] ?? Number.POSITIVE_INFINITY;

                if (priorityA !== priorityB) {
                    return priorityA - priorityB;
                }

                return EN_LANGUAGE_SORT_NAME[a].localeCompare(
                    EN_LANGUAGE_SORT_NAME[b],
                    'en',
                    {
                        sensitivity: 'base',
                    },
                );
            }),
        [],
    );

    const handleMainMenuClick = (event: React.MouseEvent<HTMLElement>) => {
        setLanguageMenuAnchorEl(null);
        setMainMenuAnchorEl(event.currentTarget);
    };

    const handleMainMenuClose = () => {
        setMainMenuAnchorEl(null);
    };

    const handleLanguageMenuClick = (event: React.MouseEvent<HTMLElement>) => {
        setMainMenuAnchorEl(null);
        setLanguageMenuAnchorEl(event.currentTarget);
    };

    const handleLanguageMenuClose = () => {
        setLanguageMenuAnchorEl(null);
    };

    const handlePrivacy = () => {
        handleMainMenuClose();
        onPrivacyClick();
    };

    const handleTerms = () => {
        handleMainMenuClose();
        onTermsClick();
    };

    const handleFaq = () => {
        handleMainMenuClose();
        onFaqClick?.();
    };

    const handleHeicConverter = () => {
        handleMainMenuClose();
        navigate(`/${locale}/heic-to-jpeg-converter`);
    };

    const handleContactSupport = () => {
        void firebaseService.logEvent('contact_support_click', {
            button_location: 'header_menu',
            page_path: location.pathname,
        });
        handleMainMenuClose();
    };

    const handleLanguageChange = (nextLocale: Locale) => {
        window.localStorage.setItem(LOCALE_STORAGE_KEY, nextLocale);
        void loadLocaleBundle(nextLocale);
        const nextPath = localizePath(location.pathname, nextLocale);
        navigate(`${nextPath}${location.search}${location.hash}`);
        handleLanguageMenuClose();
    };

    return (
        <AppBar
            position="sticky"
            elevation={0}
            sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.88)',
                borderBottom: '1px solid rgba(15, 23, 42, 0.08)',
                backdropFilter: 'blur(18px)',
                zIndex: (theme) => theme.zIndex.drawer + 1,
            }}
        >
            <Toolbar
                sx={{ justifyContent: 'space-between', minHeight: '64px' }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        cursor: 'pointer',
                    }}
                    onClick={() => navigate(`/${locale}`)}
                >
                    <img
                        src="/logo96.png?v=1"
                        alt="FilePlease Logo"
                        style={{ width: '32px', height: '32px' }}
                    />
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: '800',
                            color: '#0f172a',
                            fontSize: { xs: '1.15rem', sm: '1.25rem' },
                            letterSpacing: 0,
                        }}
                    >
                        FilePlease
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <IconButton
                        size="large"
                        edge="start"
                        aria-label="language menu"
                        onClick={handleLanguageMenuClick}
                        sx={{ color: '#424242', gap: 0.5, px: 1.25 }}
                    >
                        <LanguageIcon />
                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: 700,
                                letterSpacing: '0.06em',
                                lineHeight: 1,
                            }}
                        >
                            {LOCALE_BADGE_LABEL[locale]}
                        </Typography>
                    </IconButton>
                    <IconButton
                        size="large"
                        edge="start"
                        aria-label="menu"
                        onClick={handleMainMenuClick}
                        sx={{ color: '#424242' }}
                    >
                        <MenuIcon />
                    </IconButton>
                    <Menu
                        id="language-menu"
                        anchorEl={languageMenuAnchorEl}
                        open={languageMenuOpen}
                        onClose={handleLanguageMenuClose}
                        MenuListProps={{
                            dense: true,
                            sx: { py: 0.5 },
                        }}
                        PaperProps={{
                            sx: {
                                borderRadius: '12px',
                                mt: 1.5,
                                minWidth: 250,
                                boxShadow: '0px 5px 15px rgba(0,0,0,0.1)',
                            },
                        }}
                    >
                        <MenuItem
                            disabled
                            sx={{ opacity: 1, py: 0.75, minHeight: 30 }}
                        >
                            <Typography
                                variant="caption"
                                sx={{
                                    fontWeight: 700,
                                    letterSpacing: '0.04em',
                                    color: '#555',
                                }}
                            >
                                {t('menu.language')}
                            </Typography>
                        </MenuItem>

                        {sortedLanguageLocales.map((lng) => (
                            <MenuItem
                                key={lng}
                                onClick={() => handleLanguageChange(lng)}
                                selected={lng === locale}
                                sx={{
                                    py: 0.75,
                                    minHeight: 34,
                                    fontSize: '0.92rem',
                                }}
                            >
                                {languageSwitcherOptions[lng]}
                            </MenuItem>
                        ))}
                    </Menu>
                    <Menu
                        id="main-menu"
                        anchorEl={mainMenuAnchorEl}
                        open={mainMenuOpen}
                        onClose={handleMainMenuClose}
                        PaperProps={{
                            sx: {
                                borderRadius: '12px',
                                mt: 1.5,
                                minWidth: 220,
                                boxShadow: '0px 5px 15px rgba(0,0,0,0.1)',
                            },
                        }}
                    >
                        <MenuItem onClick={handleFaq} sx={{ py: 1.5 }}>
                            FAQ
                        </MenuItem>
                        <MenuItem
                            onClick={handleHeicConverter}
                            sx={{ py: 1.5 }}
                        >
                            {t('menu.heicToJpegConverter')}
                        </MenuItem>
                        <MenuItem onClick={handlePrivacy} sx={{ py: 1.5 }}>
                            {t('menu.privacyPolicy')}
                        </MenuItem>
                        <MenuItem onClick={handleTerms} sx={{ py: 1.5 }}>
                            {t('menu.termsOfService')}
                        </MenuItem>
                        <MenuItem
                            component="a"
                            href={SUPPORT_MAILTO}
                            onClick={handleContactSupport}
                            sx={{ py: 1.5 }}
                        >
                            {t('menu.contactSupport')}
                        </MenuItem>
                    </Menu>
                </Box>
            </Toolbar>
        </AppBar>
    );
};

export default Header;
